import { create } from "zustand";
import { subscriptionApi, type SubscriptionConfigV3Response, type DtoPriceV3 } from "~/api/subscription";
import { devLog } from "~/lib/dev-log";
import * as Sentry from "@sentry/react";
import { ApiClientError, apiErrorMessage } from "~/api/client";

export interface SubscriptionConfigState {
  // Data state
  subscriptionConfig: SubscriptionConfigV3Response | null;
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;

  // Actions
  fetchSubscriptionConfig: () => Promise<void>;
  clearError: () => void;
  reset: () => void;

  // Atomic operations
  trySetLoading: () => boolean;
  setNotLoading: () => void;
}

// Cache duration: 5 minutes
const CACHE_DURATION = 5 * 60 * 1000;

/**
 * Get the default (first/lowest tier) Stripe price from the V3 config.
 */
export function getDefaultStripePrice(config: SubscriptionConfigV3Response | null): DtoPriceV3 | undefined {
  return config?.stripeConfig?.prices?.[0];
}

/**
 * Get the trial days from the V3 config.
 */
export function getTrialDays(config: SubscriptionConfigV3Response | null): number {
  return config?.stripeConfig?.trialDays ?? 3;
}

export const useSubscriptionConfigStore = create<SubscriptionConfigState>()((set, get) => {
  const isCacheValid = (): boolean => {
    const { lastFetched } = get();
    if (!lastFetched) return false;
    return Date.now() - lastFetched < CACHE_DURATION;
  };

  const logSubscriptionEvent = (event: string, data?: any) => {
    devLog(`[Subscription Store] ${event}`, data);
  };

  return {
    subscriptionConfig: null,
    isLoading: false,
    error: null,
    lastFetched: null,

    trySetLoading: () => {
      const state = get();
      if (state.isLoading) {
        logSubscriptionEvent("Loading skipped - already loading");
        return false;
      }
      set({ isLoading: true, error: null });
      return true;
    },

    setNotLoading: () => {
      set({ isLoading: false });
    },

    fetchSubscriptionConfig: async () => {
      const state = get();
      if (state.subscriptionConfig && isCacheValid()) {
        logSubscriptionEvent("Using cached subscription config");
        return;
      }

      if (!get().trySetLoading()) {
        return;
      }

      try {
        logSubscriptionEvent("Fetching subscription config (v3)");
        const config = await subscriptionApi.getSubscriptionConfig();

        set({
          subscriptionConfig: config,
          lastFetched: Date.now(),
          error: null,
        });

        logSubscriptionEvent("Successfully fetched subscription config", {
          trialDays: config.stripeConfig.trialDays,
          stripePricesCount: config.stripeConfig.prices.length,
          paypalPlansCount: config.paypalConfig.plans?.length ?? 0,
        });
      } catch (error) {
        set({ error: apiErrorMessage(error, "Failed to fetch subscription config") });
        logSubscriptionEvent("Failed to fetch subscription config", error);

        // The client already reported an ApiClientError with full context and a
        // fingerprint; only report here when it did not (an unexpected bug), while
        // keeping the operation tag that `reportUnexpectedError` does not carry.
        if (!(error instanceof ApiClientError)) {
          Sentry.captureException(error, {
            tags: {
              operation: "fetch_subscription_config",
            },
          });
        }

        throw error;
      } finally {
        get().setNotLoading();
      }
    },

    clearError: () => {
      set({ error: null });
    },

    reset: () => {
      logSubscriptionEvent("Resetting subscription store");
      set({
        subscriptionConfig: null,
        isLoading: false,
        error: null,
        lastFetched: null,
      });
    },
  };
});
